import type { CryptoPrice } from '@/features/assets/api/types'

const REST_URL = 'https://api.binance.com/api/v3/ticker/24hr'
const KLINES_URL = 'https://api.binance.com/api/v3/klines'
const WS_URL = 'wss://stream.binance.com:9443/stream'

const toPair = (symbol: string) => `${symbol}USDT`
const fromPair = (pair: string) => pair.endsWith('USDT') ? pair.slice(0, -4) : pair

interface BinanceTicker24h {
  symbol: string
  lastPrice: string
  closeTime: number
}

/**
 * Fetch snapshot 1 lần từ Binance REST. Client gọi trực tiếp (CORS open).
 * Chỉ fetch các symbol cần thiết để giảm weight.
 */
export async function fetchBinancePricesRest(symbols: string[]): Promise<CryptoPrice[]> {
  if (symbols.length === 0) return []
  const pairs = symbols.map(toPair)
  const url = `${REST_URL}?symbols=${encodeURIComponent(JSON.stringify(pairs))}`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Binance REST ${res.status}`)
  const data = (await res.json()) as BinanceTicker24h[]
  if (!Array.isArray(data)) throw new Error('Binance REST: invalid response')
  return data.map((t) => ({
    symbol: fromPair(t.symbol),
    price: parseFloat(t.lastPrice),
    updated_at: new Date(t.closeTime).toISOString(),
  }))
}

/** Daily close price từ Binance klines. Cache trong memory module-level. */
export interface DailyClose {
  date: string  // YYYY-MM-DD (UTC)
  close: number
}

const klinesCache = new Map<string, DailyClose[]>()

function dateToMs(dateStr: string): number {
  return new Date(`${dateStr}T00:00:00Z`).getTime()
}

function msToDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

/**
 * Fetch historical daily klines cho 1 crypto symbol từ Binance.
 * Không cần auth, CORS open. Limit max 1000 candles.
 */
export async function fetchBinanceKlines(symbol: string, from: string, to: string): Promise<DailyClose[]> {
  const cacheKey = `${symbol}:${from}:${to}`
  const cached = klinesCache.get(cacheKey)
  if (cached) return cached

  const startTime = dateToMs(from)
  const endTime = dateToMs(to) + 86400_000  // inclusive to
  const url = `${KLINES_URL}?symbol=${toPair(symbol)}&interval=1d&startTime=${startTime}&endTime=${endTime}&limit=1000`

  const res = await fetch(url)
  if (!res.ok) throw new Error(`Binance klines ${res.status}`)
  const raw = (await res.json()) as unknown[][]
  if (!Array.isArray(raw)) throw new Error('Binance klines: invalid response')

  // raw[i] = [openTime, open, high, low, close, volume, closeTime, ...]
  const result: DailyClose[] = raw.map((row) => ({
    date: msToDate(row[0] as number),
    close: parseFloat(row[4] as string),
  })).filter((d) => Number.isFinite(d.close) && d.close > 0)

  klinesCache.set(cacheKey, result)
  return result
}

interface WsTicker {
  s: string    // symbol BTCUSDT
  c: string    // last price
  E: number    // event time ms
}

interface WsCombinedMessage {
  stream: string
  data: WsTicker
}

/**
 * WebSocket manager cho Binance ticker stream.
 * Tự reconnect với exponential backoff. Hỗ trợ đổi danh sách symbol runtime.
 */
export class BinancePriceStream {
  private ws: WebSocket | null = null
  private symbols: string[] = []
  private onTick: ((price: CryptoPrice) => void) | null = null
  private retry = 0
  private closedByUser = false
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null

  start(symbols: string[], onTick: (price: CryptoPrice) => void): void {
    this.closedByUser = false
    this.symbols = [...symbols]
    this.onTick = onTick
    this.connect()
  }

  /** Đổi danh sách symbol — reconnect với stream mới */
  setSymbols(symbols: string[]): void {
    // So sánh nhanh, tránh reconnect không cần thiết
    const next = [...symbols].sort().join(',')
    const current = [...this.symbols].sort().join(',')
    if (next === current) return
    this.symbols = [...symbols]
    // Close và để reconnect tự dựng lại với symbols mới
    this.ws?.close()
  }

  stop(): void {
    this.closedByUser = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.ws?.close()
    this.ws = null
    this.onTick = null
  }

  private connect(): void {
    if (this.symbols.length === 0 || !this.onTick) return

    const streams = this.symbols
      .map((s) => `${toPair(s).toLowerCase()}@ticker`)
      .join('/')
    const url = `${WS_URL}?streams=${streams}`

    try {
      this.ws = new WebSocket(url)
    } catch {
      this.scheduleReconnect()
      return
    }

    this.ws.onopen = () => {
      this.retry = 0
    }

    this.ws.onmessage = (ev) => {
      if (!this.onTick) return
      try {
        const msg = JSON.parse(ev.data) as WsCombinedMessage | WsTicker
        const t = 'data' in msg ? msg.data : msg
        if (!t?.s || !t?.c) return
        const price = parseFloat(t.c)
        if (!Number.isFinite(price) || price <= 0) return
        this.onTick({
          symbol: fromPair(t.s),
          price,
          updated_at: new Date(t.E ?? Date.now()).toISOString(),
        })
      } catch {
        // ignore parse errors
      }
    }

    this.ws.onclose = () => {
      if (this.closedByUser) return
      this.scheduleReconnect()
    }

    this.ws.onerror = () => {
      this.ws?.close()
    }
  }

  private scheduleReconnect(): void {
    if (this.closedByUser) return
    const delay = Math.min(1000 * 2 ** this.retry, 30_000)
    this.retry++
    this.reconnectTimer = setTimeout(() => this.connect(), delay)
  }
}
